import { FC } from 'react'
import PaginationLayout from '@components/PaginationLayout'
import SkeletonCards from '@components/SkeletonCards'
import ItemCard from '@components/Cards/BidItem'
import getPagination from '@utilities/functions/getPagination'
import BidModal from '@components/BidModal/BidModal'
import BanModal from '@components/BanModal/BanModal'
import useModal from '@components/Items/utils/hooks/useModal'
import useQuery from '@components/Items/utils/hooks/useQuery'
import useItems from '@components/Items/utils/hooks/useItems'
import { filterType, dataType, ItemQuery } from '@components/Items/types'
import { FilterProvider } from '@components/Items/Filters/Context'
import useFilters from '@components/Items/utils/hooks/useFilters'
import FiltersLayout from '@components/Items/FiltersLayout'
import useGtmItemSearchEvent from '@utilities/hooks/useGtmItemSearchEvent'
import { ItemSearchQueries } from '@utilities/functions/buildSearchItemTagData'

export type PropTypes = {
  initialData: dataType
  initialFilter: filterType
  staticQueries?: ItemQuery
}

type Props = FC<PropTypes>

const Items: Props = ({ initialData, initialFilter, staticQueries = {} }) => {
  const [
    {
      showBidModal,
      showBanModal,
      negativeZBid,
      negativeZBan,
      modalAction,
      modalData,
    },
    { toggleModal, setModalAction, setModalData },
  ] = useModal()

  const [query, { updateQuery, clearQuery }] = useQuery()

  const [{ items, isLoading, totalCount }, setItems] = useItems(
    initialData,
    query,
    staticQueries
  )

  const { filterData, isLoading: filterDataIsLoading } = useFilters(
    initialFilter,
    query,
    staticQueries
  )

  const {
    locations,
    conditions,
    pickupDates,
    auctionEndDates,
    itemCategories,
  } = filterData

  useGtmItemSearchEvent(query as ItemSearchQueries, filterData)

  const pageChange = (pageId: number) => {
    if (pageId === Number(query.pageId) || (!query.pageId && pageId === 1))
      return
    updateQuery('pageId', pageId)
  }

  return (
    <>
      <FilterProvider
        conditionList={conditions}
        locationList={locations}
        itemCategoriesList={itemCategories}
        pickupDateList={pickupDates}
        endDateList={auctionEndDates}
        query={query}
        updateQuery={updateQuery}
        clearQuery={clearQuery}
        isLoading={filterDataIsLoading}
      >
        <FiltersLayout totalCount={totalCount}>
          <PaginationLayout
            {...getPagination(totalCount, Number(query.pageId) || 1)}
            pageChange={pageChange}
            hideTopNavigation={true}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5 md:gap-6">
              {isLoading ? (
                <SkeletonCards />
              ) : items?.length ? (
                items.map((item: any) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    options={{
                      isListView: false,
                      isSavedSearchPage: true,
                    }}
                    handlers={{
                      toggleModal,
                      setModalAction,
                      setModalData,
                    }}
                    location={item?.auctionLocation}
                  />
                ))
              ) : (
                <div
                  className="flex flex-col justify-center items-center col-span-3"
                  style={{ minHeight: '40vh' }}
                >
                  <div className="text-center mt-4">
                    <h2 className="text-4xl font-bold">
                      Oops! It looks like there are no available items matching
                      your request.
                    </h2>
                    <p className={'xl'}>
                      Please search something else to get a response.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </PaginationLayout>
          <BidModal
            showModal={showBidModal}
            negativeZ={negativeZBid}
            toggleModal={toggleModal}
            modalAction={modalAction}
            modalData={modalData}
          />
          <BanModal
            showModal={showBanModal}
            negativeZ={negativeZBan}
            toggleModal={toggleModal}
            modalData={modalData}
          />
        </FiltersLayout>
      </FilterProvider>
    </>
  )
}

export default Items
